import React from 'react';
import { Box, Typography, Grid, Chip } from '@mui/joy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { PAYMENT_METHODS } from '../constants/paymentMethods';
import { formatTime } from '../services/format';
import i18n from '../../../i18n';
import { OmrAmount } from '../components/OmrAmount';

function formatPaymentDateTime(dateString) {
  if (!dateString) return '-';
  try {
    return formatTime(dateString);
  } catch (e) {
    return dateString;
  }
}

function tUi(key, opts, tFromProps) {
  const tr = typeof tFromProps === 'function' ? tFromProps : (k, o) => i18n.t(k, { ns: 'ui', ...o });
  return tr(key, opts);
}

/**
 * Read-only payment columns aligned with PaymentsScreen (excluding Actions).
 * @param {function} [t] — optional `useTranslation('ui')` t for reactive language switches
 */
export function getShipmentFormPaymentColumns(t) {
  return [
    { field: 'id', headerName: tUi('shipmentForm.payColId', {}, t), flex: 1, minWidth: 80 },
    {
      field: 'invoice',
      headerName: tUi('shipmentForm.payColInvoice', {}, t),
      flex: 1.5,
      minWidth: 100,
      renderCell: (params) => (params.row.invoice ? `#${params.row.invoice}` : '-'),
    },
    {
      field: 'shipments',
      headerName: tUi('shipmentForm.payColShipments', {}, t),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => {
        const shipments = params.row.shipments || [];
        if (shipments.length === 0) return '-';
        return (
          <Box>
            <Grid container spacing={0.5} sx={{ width: '100%' }}>
              {shipments.map((shipment) => {
                // Payment rows carry a shipment summary object, not a bare id.
                const shipmentId = shipment != null && typeof shipment === 'object' ? shipment.id : shipment;
                return (
                  <Grid item key={shipmentId}>
                    <Chip
                      variant="soft"
                      size="sm"
                      sx={{
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                      }}
                    >
                      #{shipmentId}
                    </Chip>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      },
    },
    {
      field: 'payment_method',
      headerName: tUi('shipmentForm.payColPaymentMethod', {}, t),
      flex: 1.5,
      minWidth: 130,
      renderCell: (params) => (
        <Chip size="small" variant="soft" color="primary">
          {PAYMENT_METHODS[params.row.payment_method] || params.row.payment_method}
        </Chip>
      ),
    },
    {
      field: 'amount',
      headerName: tUi('shipmentForm.payColAmount', {}, t),
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => {
        const amount = parseFloat(params.row.amount || 0);
        const refunded = parseFloat(params.row.refunded_amount || '0');
        const net = amount - refunded;
        const isRefunded = params.row.is_refunded === true;
        return (
          <Box>
            <Typography fontWeight="md" color="success" component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <OmrAmount value={net} />
            </Typography>
            {isRefunded && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }} component="div">
                <OmrAmount value={amount} />
                <span aria-hidden>−</span>
                <OmrAmount value={refunded} />
                <span>{tUi('shipmentForm.payAmountRefundedLineSuffix', {}, t)}</span>
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'refunded_amount',
      headerName: tUi('shipmentForm.payColRefunded', {}, t),
      flex: 1.2,
      minWidth: 100,
      renderCell: (params) => {
        const refunded = parseFloat(params.row.refunded_amount || '0');
        if (refunded <= 0) return '-';
        return (
          <Typography level="body-sm" color="danger" component="div">
            <OmrAmount value={refunded} />
          </Typography>
        );
      },
    },
    {
      field: 'is_refunded',
      headerName: tUi('shipmentForm.payColRefundStatus', {}, t),
      flex: 1.2,
      minWidth: 120,
      renderCell: (params) => {
        const isRefunded = params.row.is_refunded === true;
        if (!isRefunded) return '-';
        const amount = parseFloat(params.row.amount || 0);
        const refunded = parseFloat(params.row.refunded_amount || '0');
        const isPartial = refunded > 0 && refunded < amount;
        return (
          <Chip
            size="sm"
            variant="soft"
            color="warning"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          >
            {isPartial ? tUi('shipmentForm.payRefundPartial', {}, t) : tUi('shipmentForm.payRefundFull', {}, t)}
          </Chip>
        );
      },
    },
    {
      field: 'refund_reason',
      headerName: tUi('shipmentForm.payColRefundReason', {}, t),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => {
        const reason = params.row.refund_reason;
        if (!reason) return '-';
        return (
          <Typography level="body-sm" sx={{ maxWidth: 200 }} noWrap title={reason}>
            {reason}
          </Typography>
        );
      },
    },
    {
      field: 'transaction_reference',
      headerName: tUi('shipmentForm.payColTransactionRef', {}, t),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => params.row.transaction_reference || '-',
    },
    {
      field: 'transaction_date',
      headerName: tUi('shipmentForm.payColTransactionDate', {}, t),
      flex: 1.5,
      minWidth: 120,
      renderCell: (params) => params.row.transaction_date || '-',
    },
    {
      field: 'account_name',
      headerName: tUi('shipmentForm.payColAccountName', {}, t),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => params.row.account_name || '-',
    },
    {
      field: 'is_confirmed',
      headerName: tUi('shipmentForm.payColStatus', {}, t),
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => {
        const isConfirmed = params.row.is_confirmed;
        return (
          <Chip
            size="sm"
            variant={isConfirmed ? 'solid' : 'outlined'}
            color={isConfirmed ? 'success' : 'neutral'}
            startDecorator={isConfirmed ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              ...(isConfirmed
                ? {
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                  }
                : {
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                  }),
            }}
          >
            {isConfirmed ? tUi('shipmentForm.payStatusConfirmed', {}, t) : tUi('shipmentForm.payStatusUnconfirmed', {}, t)}
          </Chip>
        );
      },
    },
    {
      field: 'confirmed_by_name',
      headerName: tUi('shipmentForm.payColConfirmedBy', {}, t),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => {
        if (!params.row.is_confirmed) return '-';
        return (
          <Typography level="body-sm" sx={{ color: params.row.confirmed_by_name ? 'text.primary' : 'text.tertiary' }}>
            {params.row.confirmed_by_name || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'confirmed_at',
      headerName: tUi('shipmentForm.payColConfirmedAt', {}, t),
      flex: 2,
      minWidth: 160,
      renderCell: (params) => {
        if (!params.row.is_confirmed || !params.row.confirmed_at) return '-';
        return (
          <Typography level="body-sm" sx={{ color: 'text.primary' }}>
            {formatPaymentDateTime(params.row.confirmed_at)}
          </Typography>
        );
      },
    },
    {
      field: 'created_at',
      headerName: tUi('shipmentForm.payColCreatedAt', {}, t),
      flex: 2,
      minWidth: 160,
      renderCell: (params) => formatPaymentDateTime(params.row.created_at),
    },
  ];
}
